import React, { FC } from 'react';
import { Banner, Text, BulletList } from '@blueprint/ui';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Link from 'next/link';
import { SearchPageType } from '../SearchContext';

const StyledLink = styled('a')(({ theme: { colors, type, fontWeight } }) => {
    return css`
        line-height: ${type.lineHeight};
        color: ${colors['primary-brand']};
        text-decoration: none;
        font-weight: ${fontWeight['bold']};
    `;
});

const countyNames = (searchType: SearchPageType) => ({
    link1: {
        id: 'countynames-link1',
        text: 'Essex',
        link: `/${searchType}/property/essex/`
    },
    link2: {
        id: 'countynames-link2',
        text: 'Bedfordshire',
        link: `/${searchType}/property/bedfordshire/`
    },
    link3: {
        id: 'countynames-link3',
        text: 'Powys',
        link: `/${searchType}/property/powys/`
    }
});

const cities = (searchType: SearchPageType) => ({
    link1: {
        id: 'cities-link1',
        text: 'London',
        link: `/${searchType}/property/london/`
    },
    link2: {
        id: 'cities-link2',
        text: 'Perth',
        link: `/${searchType}/property/perth-and-kinross/perth/`
    },
    link3: {
        id: 'cities-link3',
        text: 'Beckington',
        link: `/${searchType}/property/beckington/?q=Beckington`
    }
});

const londonSuburbs = (searchType: SearchPageType) => ({
    link1: {
        id: 'london-suburbs-link1',
        text: 'Putney',
        link: `/${searchType}/property/putney/`
    },
    link2: {
        id: 'london-suburbs-link2',
        text: 'Leytonstone',
        link: `/${searchType}/property/leytonstone/`
    }
});

const postCodes = (searchType: SearchPageType) => ({
    link1: {
        id: 'postcodes-link1',
        text: 'EH2 1JX',
        link: `/${searchType}/property/new-town/queen-street/eh2-1jx/`
    },
    link2: {
        id: 'postcodes-link2',
        text: 'M20',
        link: `/${searchType}/property/m20/`
    }
});

const createLink = (
    text: { [link: string]: { id: string; text: string; link: string } },
    link: string
) => (
    <Link key={text[link].id} href={text[link].link} passHref>
        <StyledLink>{text[link].text}</StyledLink>
    </Link>
);

interface Props {
    areaName?: string;
    searchType: SearchPageType;
}

export const ErrorArea: FC<Props> = ({ areaName = '', searchType }) => {
    return (
        <Banner
            data-testid="area-error-banner"
            strapline="No results found"
            title={`Sorry we could not find a place name matching ‘${areaName}’.`}
            text="Please check your spelling or enter another location and search again."
        >
            <Text isBold>
                Our search recognises the following types of place names:
            </Text>
            <BulletList>
                <li>
                    County names, e.g.{' '}
                    {createLink(countyNames(searchType), 'link1')},{' '}
                    {createLink(countyNames(searchType), 'link2')},{' '}
                    {createLink(countyNames(searchType), 'link3')}
                </li>
                <li>
                    City, town and village names, e.g.{' '}
                    {createLink(cities(searchType), 'link1')},{' '}
                    {createLink(cities(searchType), 'link2')},{' '}
                    {createLink(cities(searchType), 'link3')}
                </li>
                <li>
                    London suburb and area names, e.g.{' '}
                    {createLink(londonSuburbs(searchType), 'link1')},{' '}
                    {createLink(londonSuburbs(searchType), 'link2')}
                </li>
                <li>
                    Full and partial postcodes, e.g.{' '}
                    {createLink(postCodes(searchType), 'link1')},{' '}
                    {createLink(postCodes(searchType), 'link2')}
                </li>
            </BulletList>
            <Text>
                If you experience any difficulties or need further assistance,
                please <StyledLink href="/contact/">Contact us</StyledLink>.
            </Text>
        </Banner>
    );
};
